import React, { useState, useEffect } from 'react';

import { Card, Space, Table, Tag, Segmented, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, Link } from 'react-router-dom';
import ListPageHeader from '@shared/ListPageHeader';
import ProductInventorySummary from '@components/shared/products/ProductInventorySummary';
import ProductInventoryTable from '@components/shared/products/ProductInventoryTable';
import ProductShelfDistribution from '@components/shared/products/ProductShelfDistribution';

import operatorApiFetch from '@services/operator/apiService';
import { inventoryService } from '@services/operator/inventoryService';
import { inventoryHoldService } from '@services/operator/inventoryHoldService';
import { productService } from '@services/operator/productService';

export default function ProductInventory() {
  const product = useLoaderData();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [inventories, setInventories] = useState([]);
  const [inTransitItems, setInTransitItems] = useState([]);
  const [loadingInTransit, setLoadingInTransit] = useState(false);
  // 預設停在「儲位分佈」——這頁最該被看到的是「商品放在哪幾個儲位、各幾個」
  const [view, setView] = useState('by_shelf');

  useEffect(() => {
    const fetchInventories = async () => {
      setLoading(true);
      try {
        const response = await operatorApiFetch('inventories/get_records', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'products.id': [product.id],
            page: 1,
            page_size: 100,
            sorting_field: 'inventories.id',
            sorting_direction: 'desc'
          })
        });

        if (!response.ok) {
          throw new Error('Failed to fetch inventories');
        }

        const data = await response.json();
        if (data.success) {
          setInventories(data.data || []);
        } else {
          throw new Error(data.message || 'Failed to load inventories');
        }
      } catch (error) {
        console.error('Error fetching inventories:', error);
        message.error(t('common.error.load_failed'));
      } finally {
        setLoading(false);
      }
    };

    const fetchInTransit = async () => {
      setLoadingInTransit(true);
      try {
        const result = await productService.fetchInTransit(product.id);
        if (result.success) {
          setInTransitItems(result.data || []);
        }
      } catch (error) {
        console.error('Error fetching in-transit items:', error);
      } finally {
        setLoadingInTransit(false);
      }
    };

    if (product?.id) {
      fetchInventories();
      fetchInTransit();
    }
  }, [product?.id, t]);

  const inTransitColumns = [
    {
      title: t('models.inbound.name'),
      dataIndex: 'inbound_name',
      key: 'inbound_name',
      render: (text, record) => (
        <Link to={`/inbounds/${record.inbound_id}`} style={{ color: '#1890ff' }}>
          {text}
        </Link>
      ),
    },
    {
      title: t('models.inbound.status'),
      dataIndex: 'inbound_status',
      key: 'inbound_status',
      render: (status) => (
        <Tag color={status === 'processing' ? 'orange' : 'blue'}>
          {t(`enums.inbound.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('models.inbound.eta'),
      dataIndex: 'eta',
      key: 'eta',
      render: (text) => text || '-',
    },
    {
      title: t('models.inventory.inventory_type'),
      dataIndex: 'inventory_type',
      key: 'inventory_type',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: 'batch',
      key: 'batch',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: t('models.inbound_item.scheduled_quantity'),
      dataIndex: 'scheduled_quantity',
      key: 'scheduled_quantity',
      align: 'right',
    },
    {
      title: t('models.inbound_item.putaway_quantity'),
      dataIndex: 'putaway_quantity',
      key: 'putaway_quantity',
      align: 'right',
    },
  ];

  return (
    <div>
      <ListPageHeader
        items={['products', 'products.list', { label: product.sku + ' - ' + product.name }, { label: t('shared.inventory.title') }]}
      />

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <ProductInventorySummary inventories={inventories} />

          {/* 視角切換：儲位分佈（在哪、各幾個）/ 庫存批次（批號、效期、可用、碎片） */}
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { label: t('shared.inventory.view.by_shelf'), value: 'by_shelf' },
              { label: t('shared.inventory.view.by_batch'), value: 'by_batch' },
            ]}
          />

          {/* In Stock Inventory */}
          {view === 'by_shelf' ? (
            <ProductShelfDistribution
              inventories={inventories}
              loading={loading}
              merchantId={product.merchant_id}
              role="operator"
            />
          ) : (
            <ProductInventoryTable
              inventories={inventories}
              loading={loading}
              inventoryService={inventoryService}
              holdService={inventoryHoldService}
              merchantId={product.merchant_id}
              role="operator"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => t('common.pagination.total', { total }),
              }}
            />
          )}

          {/* In Transit Inventory */}
          <div>
            <div style={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '16px' }}>
              {t('shared.inventory.in_transit')} ({inTransitItems.length})
            </div>
            <Table
              columns={inTransitColumns}
              dataSource={inTransitItems}
              rowKey="id"
              loading={loadingInTransit}
              pagination={false}
              locale={{ emptyText: t('common.table.no_data') }}
            />
          </div>
        </Space>
      </Card>
    </div>
  );
}
